import genericUtility from "../utility/GenericUtility";

import AppConfig from "../../config/config";

const serverAddress = AppConfig.apiDomain;


// ---------------------------------------------------------------------------
// Universal 401 interceptor
// Register once via setLogoutCallback (called from useAutoLogout hook in App).
// All request helpers fire this when the backend returns 401 (expired JWT).
// ---------------------------------------------------------------------------
let logoutCallback: (() => void) | null = null;

export function setLogoutCallback(fn: (() => void) | null): void {
  logoutCallback = fn;
}

function handle401(response: Response): void {
  if (response.status !== 401 || !logoutCallback) return;
  // Only force-logout if the user was actually logged in. A 401 returned for a
  // guest (no token) should not redirect — that bounces guests off public pages
  // like product details when an unrelated authenticated call fails.
  const token = genericUtility.getCookie("token");
  if (!token) return;
  logoutCallback();
}

export async function postRequest(
  endpoint: string,
  payload: any,
  includeBearerToken: boolean,
  // Additive, optional: lets a caller add request headers this client
  // doesn't set on its own (e.g. If-Match, Idempotency-Key for the slot
  // availability calendar's optimistic-concurrency writes). Every existing
  // call site omits this and is unaffected.
  extraHeaders?: Record<string, string>,
  // Additive, optional: abort signal and keepalive for callers that need them
  // (voice analyze is cancellable; voice outcome reports must survive the
  // popup closing). Omitted -> the fetch call is exactly what it was before.
  init?: { signal?: AbortSignal; keepalive?: boolean }
): Promise<Response> {
  const url = serverAddress + endpoint;
  const token = genericUtility.getCookie("token");

  // 1. Check if the payload is for a file upload (FormData)
  const isFormData = payload instanceof FormData;

  const headers: HeadersInit = {};

  if (includeBearerToken && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // 2. IMPORTANT: Only set Content-Type to application/json if it's NOT FormData.
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }

  // 3. Prepare the body: stringify JSON, otherwise pass FormData directly
  const body = isFormData ? payload : JSON.stringify(payload);

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: body,
    ...(init?.signal ? { signal: init.signal } : {}),
    ...(init?.keepalive ? { keepalive: true } : {}),
  });

  handle401(response);
  return response;
}

/**
 * GET request function
 */
export async function getRequest(
  endpoint: string,
  includeBearerToken: boolean,
  // Additive, optional - see postRequest's extraHeaders doc above.
  extraHeaders?: Record<string, string>
): Promise<Response> {
  const url = serverAddress + endpoint;
  const token = genericUtility.getCookie("token")?.replace(" path=/", "");

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (includeBearerToken && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
    // This client talks to an authenticated API, not a CDN/static host — GET
    // responses (e.g. subscription/order status that can change server-side
    // via a webhook) must never be served from the browser's HTTP cache.
    cache: "no-store",
  });

  handle401(response);
  return response;
}

export async function putRequest(
  endpoint: string,
  payload: any,
  includeBearerToken: boolean,
  // Additive, optional - see postRequest's extraHeaders doc above.
  extraHeaders?: Record<string, string>
): Promise<Response> {
  const url = serverAddress + endpoint;
  const token = genericUtility.getCookie("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (includeBearerToken && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }
  const response = await fetch(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(payload),
  });

  handle401(response);
  return response;
}

/**
 * PATCH request function. Additive: mirrors putRequest (JSON body, bearer
 * token, 401 interceptor) for endpoints that use partial updates (the
 * catalogue import's draft edits). Never retried by callers — a PATCH carries
 * an optimistic `version`, and replaying it is the caller's decision.
 */
export async function patchRequest(
  endpoint: string,
  payload: any,
  includeBearerToken: boolean,
  extraHeaders?: Record<string, string>
): Promise<Response> {
  const url = serverAddress + endpoint;
  const token = genericUtility.getCookie("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (includeBearerToken && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }
  const response = await fetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });

  handle401(response);
  return response;
}

export async function deleteRequest(
  endpoint: string,
  includeBearerToken: boolean,
  // Additive, optional - see postRequest's extraHeaders doc above.
  extraHeaders?: Record<string, string>
): Promise<Response> {
  const url = serverAddress + endpoint;
  const token = genericUtility.getCookie("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  // Only send Authorization when logged in. A logged-out "Bearer undefined" would be
  // rejected with a 401 by the resource server even on public endpoints.
  if (includeBearerToken && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }
  const response = await fetch(url, {
    method: "DELETE",
    headers,
  });

  handle401(response);
  return response;
}

// Fetches a fully-qualified public URL (e.g. Supabase Storage CDN).
// Unlike getRequest, does not prepend apiDomain and sends no auth headers.
export async function getPublicAsset(url: string): Promise<Response> {
  const response = await fetch(url, { method: "GET" });
  return response;
}

/**
 * PUTs raw file bytes to a presigned storage URL (e.g. the `uploadUrl` returned
 * by /api/v1/storage/upload). Like getPublicAsset, this targets a fully-qualified
 * URL directly — it does NOT prepend apiDomain and sends no Bearer token (the
 * presigned URL carries its own short-lived authorization). The Content-Type
 * must match what was declared when the URL was minted.
 */
export async function putToSignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string
): Promise<Response> {
  return fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
}

export default {
  postRequest,
  putRequest,
  getRequest,
  deleteRequest,
  getPublicAsset,
  putToSignedUrl,
};
